import { piecesOf } from '@familyhub/shared';

/**
 * The board — our own, not chessground (GPL-3, and Lichess's look). A grid of
 * 64 buttons, so every square has an accessible name ("e4, witte loper") and the
 * screen tests can tap it like a person. Tap a piece, then tap where it goes:
 * on a phone tap-tap is surer than a drag, and needs no gesture code.
 *
 * Pieces are the Cburnett set (BSD; web/public/schaak/pieces/LICENSE.md),
 * same-origin and precached.
 */

const FILES = 'abcdefgh';

const PIECE_NAMES: Record<string, string> = {
  k: 'koning',
  q: 'dame',
  r: 'toren',
  b: 'loper',
  n: 'paard',
  p: 'pion',
};

export interface Arrow {
  from: string;
  to: string;
  tone: 'good' | 'bad';
  /** A small number where the arrow's head begins (1 = best) — heads differ even when two arrows share a start. */
  label?: string;
  /** Drawn faint, when another arrow has the focus. */
  faded?: boolean;
}

const TONE = { good: 'var(--sk-arrow-good)', bad: 'var(--sk-arrow-bad)' };

function ArrowShape({ from, to, arrow }: { from: [number, number]; to: [number, number]; arrow: Arrow }) {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const ux = (x2 - x1) / len;
  const uy = (y2 - y1) / len;
  const head = 0.42;
  const w = 0.16;
  // Shaft ends where the head starts; the head's tip stops short of the centre.
  const tipX = x2 - ux * 0.12;
  const tipY = y2 - uy * 0.12;
  const bx = tipX - ux * head;
  const by = tipY - uy * head;
  const px = -uy;
  const py = ux;
  const colour = TONE[arrow.tone];
  return (
    <g opacity={arrow.faded ? 0.25 : 0.88}>
      <line x1={x1} y1={y1} x2={bx} y2={by} stroke={colour} strokeWidth={w} strokeLinecap="round" />
      <polygon
        points={`${tipX},${tipY} ${bx + px * head * 0.62},${by + py * head * 0.62} ${bx - px * head * 0.62},${by - py * head * 0.62}`}
        fill={colour}
      />
      {arrow.label && (
        <g>
          <circle cx={bx} cy={by} r={0.2} fill={colour} />
          <text x={bx} y={by + 0.085} textAnchor="middle" fontSize={0.24} fontWeight={700} fill="#fff">
            {arrow.label}
          </text>
        </g>
      )}
    </g>
  );
}

export function pieceName(color: 'w' | 'b', type: string): string {
  // "het paard" takes no -e: wit paard, but witte loper.
  const adj = type === 'n' ? (color === 'w' ? 'wit' : 'zwart') : color === 'w' ? 'witte' : 'zwarte';
  return `${adj} ${PIECE_NAMES[type] ?? type}`;
}

export function Board({
  fen,
  orientation,
  selected,
  targets,
  lastMove,
  check,
  hint,
  highlight,
  coords = true,
  arrows = [],
  disabled,
  onSquare,
}: {
  fen: string;
  orientation: 'w' | 'b';
  selected: string | null;
  targets: readonly string[];
  lastMove: readonly [string, string] | null;
  check: string | null;
  hint?: string | null;
  /** A square to point at (the notation drills). */
  highlight?: string | null;
  /** The a–h / 1–8 labels along the edge; off when the drill is reading them. */
  coords?: boolean;
  /** Arrows drawn over the board (a mistake's review: his move, the better ones). */
  arrows?: readonly Arrow[];
  disabled?: boolean;
  onSquare: (square: string) => void;
}) {
  const pieces = new Map(piecesOf(fen).map((p) => [p.square, p]));
  const ranks = orientation === 'w' ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8];
  const files = orientation === 'w' ? [...FILES] : [...FILES].reverse();
  const targetSet = new Set(targets);

  // Square centre in board units (0–8), as seen from `orientation`.
  const centre = (sq: string): [number, number] => {
    const f = FILES.indexOf(sq[0]!);
    const r = Number(sq[1]);
    return orientation === 'w' ? [f + 0.5, 8 - r + 0.5] : [7 - f + 0.5, r - 1 + 0.5];
  };

  return (
    <div className="sk-board" role="grid" aria-label="Schaakbord">
      {arrows.length > 0 && (
        <svg className="sk-arrows" viewBox="0 0 8 8" aria-hidden>
          {arrows.map((a, i) => (
            <ArrowShape key={`${a.from}${a.to}${i}`} from={centre(a.from)} to={centre(a.to)} arrow={a} />
          ))}
        </svg>
      )}
      {ranks.map((rank, ri) => (
        <div className="sk-rank" role="row" key={rank}>
          {files.map((file, fi) => {
            const sq = `${file}${rank}`;
            const piece = pieces.get(sq);
            const dark = (FILES.indexOf(file) + rank) % 2 === 1; // a1 is dark
            const isTarget = targetSet.has(sq);
            const label = piece ? `${sq}, ${pieceName(piece.color, piece.type)}` : sq;
            return (
              <button
                key={sq}
                role="gridcell"
                type="button"
                className={[
                  'sk-sq',
                  dark ? 'is-dark' : 'is-light',
                  lastMove && (lastMove[0] === sq || lastMove[1] === sq) ? 'is-last' : '',
                  selected === sq ? 'is-selected' : '',
                  check === sq ? 'is-check' : '',
                  hint === sq ? 'is-hint' : '',
                  highlight === sq ? 'is-highlight' : '',
                ].join(' ')}
                aria-label={label}
                aria-selected={selected === sq || undefined}
                data-square={sq}
                disabled={disabled}
                onClick={() => onSquare(sq)}
              >
                {piece && (
                  <img
                    className="sk-piece"
                    src={`/schaak/pieces/${piece.color}${piece.type.toUpperCase()}.svg`}
                    alt=""
                    draggable={false}
                  />
                )}
                {isTarget && <span className={piece ? 'sk-capture' : 'sk-dot'} aria-hidden />}
                {coords && fi === 0 && <span className="sk-coord sk-coord-rank" aria-hidden>{rank}</span>}
                {coords && ri === 7 && <span className="sk-coord sk-coord-file" aria-hidden>{file}</span>}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
